/**
 * Утилиты фильтров Проектного офиса.
 */

const EMPTY_STATUS = '__empty__';

/**
 * YYYY-MM-DD для input[type=date] без сдвига из‑за UTC toISOString.
 * Для строк берём ведущий YYYY-MM-DD; для Date — UTC-календарный день
 * (API обычно отдаёт полночь UTC).
 */
export function formatDateForInput(val) {
  if (!val) return '';
  if (typeof val === 'string') {
    const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Нормализация id объектов из prefs / API. */
export function normalizeCityIds(ids) {
  return [...(ids || [])]
    .map((id) => {
      if (id === null || id === undefined || id === '') return NaN;
      return Number(id);
    })
    .filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * Разбирает строку ответственных на уникальные ФИО.
 * Разделители: запятая, точка с запятой, слэш, перевод строки, « и ».
 */
export function parseResponsibleNames(raw) {
  if (!raw || typeof raw !== 'string') return [];
  const parts = raw
    .split(/[,;/|\n]+|\s+и\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const unique = [];
  parts.forEach((name) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(name);
  });
  return unique;
}

/** Уникальный отсортированный список ответственных из задач. */
export function collectResponsibleOptions(tasks) {
  const map = new Map();
  (tasks || []).forEach((t) => {
    parseResponsibleNames(t.responsible).forEach((name) => {
      const key = name.toLowerCase();
      if (!map.has(key)) map.set(key, name);
    });
  });
  return [...map.values()].sort((a, b) => a.localeCompare(b, 'ru'));
}

export function taskMatchesResponsibleFilter(task, selectedNames) {
  if (!selectedNames || selectedNames.size === 0) return true;
  const names = parseResponsibleNames(task.responsible);
  if (names.length === 0) return false;
  const selectedLower = new Set([...selectedNames].map((n) => n.toLowerCase()));
  return names.some((n) => selectedLower.has(n.toLowerCase()));
}

export function taskMatchesStatusFilter(task, selectedStatuses) {
  if (!selectedStatuses || selectedStatuses.size === 0) return true;
  const status = (task.status || '').trim();
  if (!status) return selectedStatuses.has(EMPTY_STATUS);
  return selectedStatuses.has(status);
}

/** Начало дня UTC-локально для сравнения date input (YYYY-MM-DD). */
function dayStartMs(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getTime();
}

function dayEndMs(dateStr) {
  const start = dayStartMs(dateStr);
  if (start == null) return null;
  return start + 24 * 60 * 60 * 1000 - 1;
}

export function parseTaskDateMs(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return v instanceof Date ? v.getTime() : null;
}

export function taskMatchesSetDateRange(task, fromStr, toStr) {
  if (!fromStr && !toStr) return true;
  const ms = parseTaskDateMs(task.set_date);
  if (ms == null) return false;
  if (fromStr) {
    const from = dayStartMs(fromStr);
    if (from != null && ms < from) return false;
  }
  if (toStr) {
    const to = dayEndMs(toStr);
    if (to != null && ms > to) return false;
  }
  return true;
}

function formatDateVariants(v) {
  const ms = parseTaskDateMs(v);
  if (ms == null) return [];
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const iso = `${yyyy}-${mm}-${dd}`;
  const ru = `${dd}.${mm}.${yyyy}`;
  const ruShort = d.toLocaleDateString('ru-RU');
  return [iso, ru, ruShort];
}

/** Поиск по дате постановки / выполнения (подстрока в ISO и DD.MM.YYYY). */
export function taskMatchesDateSearch(task, rawQuery) {
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    ...formatDateVariants(task.set_date),
    ...formatDateVariants(task.completion_date),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * Разбор value селекта сортировки: `set_date_asc` → { field: 'set_date', order: 'asc' }.
 * Использует rsplit по последнему `_`, чтобы поля с `_` (set_date, due_date) не ломались.
 */
export function parseSortValue(raw) {
  const value = String(raw || '');
  const idx = value.lastIndexOf('_');
  if (idx <= 0 || idx === value.length - 1) {
    return { field: value || 'set_date', order: 'asc' };
  }
  const field = value.slice(0, idx);
  const order = value.slice(idx + 1);
  return {
    field,
    order: order === 'desc' ? 'desc' : 'asc',
  };
}

export function formatSortValue(field, order) {
  return `${field || 'set_date'}_${order === 'desc' ? 'desc' : 'asc'}`;
}

export { EMPTY_STATUS };
